import type { FixtureTimeSplitContext, TimeSplitScope } from '../types';
import { TempoBadge } from './TempoBadge';
import { TempoChart } from './TempoChart';

type TimeSplitStatsCardProps = {
  context: FixtureTimeSplitContext;
  homeTeamName: string;
  awayTeamName: string;
  scope: TimeSplitScope;
  onScopeChange: (scope: TimeSplitScope) => void;
};

const SCOPE_OPTIONS: { value: TimeSplitScope; label: string }[] = [
  { value: 'last3', label: 'Last 3' },
  { value: 'last5', label: 'Last 5' },
  { value: 'last10', label: 'Last 10' },
  { value: 'season', label: 'Season' },
];

export function TimeSplitStatsCard({
  context,
  homeTeamName,
  awayTeamName,
  scope,
  onScopeChange,
}: TimeSplitStatsCardProps) {
  // Calculate percentages directly from goal counts to ensure consistency
  const calcPct = (part: number, total: number) =>
    total > 0 ? Math.round((part / total) * 100) : 0;

  const formatPct = (pct: number) => `${pct}%`;

  // Calculate ratios from actual goal counts for display consistency
  const homeGoalsPct = {
    fh: calcPct(context.home_fh_goals_for, context.home_total_goals_for),
    sh: calcPct(context.home_total_goals_for - context.home_fh_goals_for, context.home_total_goals_for),
  };
  const homeConceededPct = {
    fh: calcPct(context.home_fh_goals_against, context.home_total_goals_against),
    sh: calcPct(context.home_total_goals_against - context.home_fh_goals_against, context.home_total_goals_against),
  };
  const awayGoalsPct = {
    fh: calcPct(context.away_fh_goals_for, context.away_total_goals_for),
    sh: calcPct(context.away_total_goals_for - context.away_fh_goals_for, context.away_total_goals_for),
  };
  const awayConcededPct = {
    fh: calcPct(context.away_fh_goals_against, context.away_total_goals_against),
    sh: calcPct(context.away_total_goals_against - context.away_fh_goals_against, context.away_total_goals_against),
  };

  return (
    <div className="time-split-card">
      <div className="time-split-card__header">
        <h3 className="time-split-card__title">Time-Split Analysis</h3>
        <div className="time-split-card__controls">
          <select
            className="time-split-card__scope-select"
            value={scope}
            onChange={(e) => onScopeChange(e.target.value as TimeSplitScope)}
          >
            {SCOPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <span
            className={`time-split-card__model ${context.model_type === 'time-split' ? 'time-split-card__model--active' : ''}`}
          >
            {context.model_type === 'time-split' ? 'Team Data' : 'League Avg'}
          </span>
        </div>
      </div>

      {/* Venue indicator */}
      <div className="time-split-card__venue-info">
        <span className="time-split-card__venue time-split-card__venue--home">
          {homeTeamName} (H) - {context.home_matches} matches
        </span>
        <span className="time-split-card__venue time-split-card__venue--away">
          {awayTeamName} (A) - {context.away_matches} matches
        </span>
      </div>

      {/* Tempo Profiles */}
      <div className="time-split-card__section">
        <h4 className="time-split-card__section-title">Tempo Profile</h4>
        <div className="time-split-card__tempo-row">
          <div className="time-split-card__tempo-team">
            <span className="time-split-card__team-name">{homeTeamName}</span>
            <TempoBadge tempo={context.home_tempo} size="md" />
          </div>
          <div className="time-split-card__tempo-team">
            <span className="time-split-card__team-name">{awayTeamName}</span>
            <TempoBadge tempo={context.away_tempo} size="md" />
          </div>
        </div>
      </div>

      {/* Goal Distribution by Half */}
      <div className="time-split-card__section">
        <h4 className="time-split-card__section-title">Goal Distribution by Half</h4>
        <div className="time-split-card__stats-grid time-split-card__stats-grid--4col">
          <div className="time-split-card__stat">
            <span className="time-split-card__stat-label">Scored in 1H</span>
            <div className="time-split-card__stat-values">
              <span className="time-split-card__stat-value time-split-card__stat-value--home">
                {context.home_fh_goals_for}/{context.home_total_goals_for} ({formatPct(homeGoalsPct.fh)})
              </span>
              <span className="time-split-card__stat-vs">vs</span>
              <span className="time-split-card__stat-value time-split-card__stat-value--away">
                {context.away_fh_goals_for}/{context.away_total_goals_for} ({formatPct(awayGoalsPct.fh)})
              </span>
            </div>
          </div>
          <div className="time-split-card__stat">
            <span className="time-split-card__stat-label">Scored in 2H</span>
            <div className="time-split-card__stat-values">
              <span className="time-split-card__stat-value time-split-card__stat-value--home">
                {context.home_total_goals_for - context.home_fh_goals_for}/{context.home_total_goals_for} ({formatPct(homeGoalsPct.sh)})
              </span>
              <span className="time-split-card__stat-vs">vs</span>
              <span className="time-split-card__stat-value time-split-card__stat-value--away">
                {context.away_total_goals_for - context.away_fh_goals_for}/{context.away_total_goals_for} ({formatPct(awayGoalsPct.sh)})
              </span>
            </div>
          </div>
          <div className="time-split-card__stat">
            <span className="time-split-card__stat-label">Conceded in 1H</span>
            <div className="time-split-card__stat-values">
              <span className="time-split-card__stat-value time-split-card__stat-value--home">
                {context.home_fh_goals_against}/{context.home_total_goals_against} ({formatPct(homeConceededPct.fh)})
              </span>
              <span className="time-split-card__stat-vs">vs</span>
              <span className="time-split-card__stat-value time-split-card__stat-value--away">
                {context.away_fh_goals_against}/{context.away_total_goals_against} ({formatPct(awayConcededPct.fh)})
              </span>
            </div>
          </div>
          <div className="time-split-card__stat">
            <span className="time-split-card__stat-label">Conceded in 2H</span>
            <div className="time-split-card__stat-values">
              <span className="time-split-card__stat-value time-split-card__stat-value--home">
                {context.home_total_goals_against - context.home_fh_goals_against}/{context.home_total_goals_against} ({formatPct(homeConceededPct.sh)})
              </span>
              <span className="time-split-card__stat-vs">vs</span>
              <span className="time-split-card__stat-value time-split-card__stat-value--away">
                {context.away_total_goals_against - context.away_fh_goals_against}/{context.away_total_goals_against} ({formatPct(awayConcededPct.sh)})
              </span>
            </div>
          </div>
        </div>
        <div className="time-split-card__data-quality">
          {context.home_time_split_available ? (
            <span className="time-split-card__quality time-split-card__quality--good">
              {homeTeamName}: ✓
            </span>
          ) : (
            <span className="time-split-card__quality time-split-card__quality--fallback">
              {homeTeamName}: default
            </span>
          )}
          {context.away_time_split_available ? (
            <span className="time-split-card__quality time-split-card__quality--good">
              {awayTeamName}: ✓
            </span>
          ) : (
            <span className="time-split-card__quality time-split-card__quality--fallback">
              {awayTeamName}: default
            </span>
          )}
        </div>
      </div>

      {/* Tempo Chart - show if at least one team has actual goal data */}
      {(context.home_time_split_available || context.away_time_split_available) && (
        <div className="time-split-card__section">
          <TempoChart
            homePeriods={context.home_period_breakdown || []}
            awayPeriods={context.away_period_breakdown || []}
            homeTeamName={homeTeamName}
            awayTeamName={awayTeamName}
            homeDataAvailable={context.home_time_split_available}
            awayDataAvailable={context.away_time_split_available}
          />
        </div>
      )}
    </div>
  );
}

export default TimeSplitStatsCard;
